import React, { useState } from "react";
import { Mail, Phone, MapPin, User } from "lucide-react";

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange }) => {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-6 h-3.5 rounded-xl border transition-colors ${
        enabled ? "bg-cyan-500 border-cyan-500" : "bg-gray-200 border-gray-300"
      }`}
    >
      <div
        className={`absolute top-0.5 w-2.5 h-2.5 bg-white rounded-full transition-transform ${
          enabled ? "translate-x-3" : "translate-x-0.5"
        }`}
      />
    </button>
  );
};

export default function ProfileNotifications({ context }) {
  const { userData } = context || {};
  const user = userData?.user || {};

  // Notification states
  const [emailNotifications, setEmailNotifications] = useState({
    securityAlerts: true,
    accountUpdates: true,
  });

  const [pushNotifications, setPushNotifications] = useState({
    loginAlerts: true,
    criticalUpdates: true,
    reminders: true,
  });

  const [systemNotifications, setSystemNotifications] = useState({
    taskUpdates: true,
    comments: true,
    mentions: true,
  });

  const handleEmailToggle = (key) => {
    setEmailNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePushToggle = (key) => {
    setPushNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSystemToggle = (key) => {
    setSystemNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex gap-4 p-8 bg-gray-50 min-h-screen">
      {/* Profile Card - Left Side */}
      <div className="w-96 bg-white rounded-lg shadow-sm border border-gray-200 h-fit">
        {/* Profile Header */}
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-start gap-4">
            <img
              src={user.avatar_url || "https://placehold.co/70x70"}
              alt="Profile"
              className="w-16 h-16 rounded object-cover"
            />
            <div>
              <h2 className="text-xl font-semibold text-zinc-800 mb-2">
                {user.full_name || "N/A"}
              </h2>
              <span className="inline-block bg-teal-400 text-white text-xs font-semibold px-4 py-1 rounded-full">
                ID: {user.user_id || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="p-5">
          <div className="flex items-start gap-2 mb-6">
            <User className="w-4 h-4 text-zinc-800" />
            <h3 className="flex items-start text-sm font-medium text-zinc-800">
              Contact Information
            </h3>
          </div>

          <div className="space-y-4">
            {/* Email */}
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-zinc-800 flex-shrink-0" />
              <div className="flex items-start gap-8 flex-1">
                <span className="flex items-start text-sm text-zinc-800 w-32">
                  Email
                </span>
                <span className="text-sm text-zinc-800">
                  {user.email || "N/A"}
                </span>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-zinc-800 flex-shrink-0" />
              <div className="flex items-start gap-8 flex-1">
                <span className="flex items-start text-sm text-zinc-800 w-32">
                  Phone
                </span>
                <span className="text-sm text-zinc-800">
                  {user.country_code || ""} {user.phone || "N/A"}
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="w-4 h-4 text-zinc-800 flex-shrink-0" />
              <div className="flex items-start gap-8 flex-1">
                <span className="flex items-start  text-sm text-zinc-800 w-32">
                  Gender
                </span>
                <span className="text-sm text-zinc-800">
                  {user.gender || "N/A"}
                </span>
              </div>
            </div>

            {/* Alternate Contact */}
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-zinc-800 flex-shrink-0" />
              <div className="flex items-center gap-8 flex-1">
                <span className="flex items-start  text-sm text-zinc-800 w-32">
                  Alternate Contact
                </span>
                <span className="text-sm text-zinc-800">
                  {user.alternate_phone || "Not provided"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile Button */}
        <div className="p-5 pt-0">
          <button className="w-full py-2 border border-gray-300 rounded text-sm text-zinc-800 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            Edit Profile
          </button>
        </div>
      </div>

      {/* Notifications Card - Right Side */}
      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="flex items-start text-2xl font-semibold text-zinc-800 mb-6">
          Notifications
        </h2>

        {/* Email Notifications Section */}
        <div className="mb-8">
          <h3 className="flex items-start text-lg font-medium text-stone-900 mb-1">
            Email Notifications
          </h3>
          <p className="flex items-start text-sm text-zinc-600 mb-4">
            Receive important updates via email
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Security Alerts</span>
              <ToggleSwitch
                enabled={emailNotifications.securityAlerts}
                onChange={() => handleEmailToggle("securityAlerts")}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Account Updates</span>
              <ToggleSwitch
                enabled={emailNotifications.accountUpdates}
                onChange={() => handleEmailToggle("accountUpdates")}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 my-6"></div>

        {/* Push Notifications Section */}
        <div className="mb-8">
          <h3 className="flex items-start text-lg font-medium text-stone-900 mb-1">
            Push Notifications
          </h3>
          <p className="flex items-start text-sm text-zinc-600 mb-4">
            Get instant notifications on your devices
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Login Alerts</span>
              <ToggleSwitch
                enabled={pushNotifications.loginAlerts}
                onChange={() => handlePushToggle("loginAlerts")}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Critical Updates</span>
              <ToggleSwitch
                enabled={pushNotifications.criticalUpdates}
                onChange={() => handlePushToggle("criticalUpdates")}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Reminders</span>
              <ToggleSwitch
                enabled={pushNotifications.reminders}
                onChange={() => handlePushToggle("reminders")}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 my-6"></div>

        {/* System Notifications Section */}
        <div>
          <h3 className="flex items-start text-lg font-medium text-stone-900 mb-1">
            System Notifications
          </h3>
          <p className="flex items-start text-sm text-zinc-600 mb-4">
            In-app notification preferences
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Task Updates</span>
              <ToggleSwitch
                enabled={systemNotifications.taskUpdates}
                onChange={() => handleSystemToggle("taskUpdates")}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Comments</span>
              <ToggleSwitch
                enabled={systemNotifications.comments}
                onChange={() => handleSystemToggle("comments")}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-800">Mentions</span>
              <ToggleSwitch
                enabled={systemNotifications.mentions}
                onChange={() => handleSystemToggle("mentions")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
